import { PROGRAMS_MEQ, PROGRAM_VENDOR_CONFIG } from 'components/common/vendorConfig';
import type { VendorDefaultPrice } from 'components/common/vendorConfig';
import { useAuthContext } from 'contexts/AuthContext';
import moment from 'moment';
import { useState, useEffect } from 'react';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import { Button, Checkbox, Form, Message, Modal, Select, TextArea } from 'semantic-ui-react';
import type { DropdownProps, InputOnChangeData } from 'semantic-ui-react';
import type { FlatPatient, FoodOrder, NumericOption, ProgramBudget, ReferralSummary } from 'types';
import { getProgramBudget, mapKey } from 'utils';

import { calculateMealPricing } from '../utils/foodUtils';

/**
 * What the create-order modal accepts.
 *
 * Established from its single JSX call site in `./index.tsx`, which renders it
 * only for a member-scoped, non-catalog vendor with nothing selected.
 *
 * The props required below are the ones that call site supplies from its own
 * module scope — `useState` values and setters, an SWR `mutate`, and functions
 * declared in the same component body — so they exist whenever this component is
 * rendered at all. The rest stay optional because they are forwarded from the
 * parent's OWN optional props and really can arrive `undefined`.
 *
 * Two props are accepted but never read, and are declared only so that call site
 * keeps compiling:
 *
 * - `loadingEstimatedDeliveryDate` (passed at line 1279) — nothing here consults
 *   it, so the estimated-delivery datepicker shows no loading state while the
 *   parent's `/vendor/estimated-delivery` query is in flight.
 * - `referralId` (line 1285) — this file recomputes its own `_referralId` from
 *   `allReferralData` in the effect below rather than using the one handed down,
 *   and even that local is unread (hence the underscore a prior pass gave it).
 *
 * Both are REPORTED, NOT FIXED: consuming either changes what the form does.
 */
type AddOrderProps = {
  /** Submits the form; the parent builds and posts the order. */
  addOrder: (event: React.FormEvent<HTMLFormElement>) => void;
  /** Referrals for the member; the in-progress one selects the budget row. */
  allReferralData?: ReferralSummary[];
  /** Re-runs the parent's `/vendor/estimated-delivery` query on mount. */
  estimatedDeliveryDateMutate: () => void;
  /** The vendor's earliest delivery date, seeded into the draft. */
  estimatedDeliveryDateRange: Date;
  /** The member, used only for the modal's header text. */
  flatPatient?: FlatPatient;
  /** Writes one field of the parent's draft order. */
  handleChange: (
    event: unknown,
    attributeName: string,
    value?: unknown,
    previous?: unknown,
  ) => void;
  /** Writes the draft's `estimatedDeliveryDate`. */
  handleEstimateDeliveryDateChange: (
    event: unknown,
    estimatedDeliveryDate: { value?: unknown },
  ) => void;
  /** Accepted but unread — see the type doc above. */
  loadingEstimatedDeliveryDate?: boolean;
  /** Modal visibility. */
  open?: boolean;
  /** The draft order being composed, held as state by the parent. */
  orderDetails: FoodOrder;
  /** Running meal-equivalent total, compared against the program maximum. */
  ordersMealEquivalents?: number;
  /** The payer org; membership in `PROGRAMS_MEQ` enables the meal-equivalent cap. */
  org?: string;
  programBudget?: ProgramBudget[];
  programMAXBudget?: number;
  programMAXMealEquivalents?: number;
  programOrdersCost?: number;
  /** Accepted but unread — see the type doc above. */
  referralId?: string;
  setOpen: (open: boolean) => void;
  setOrderDetails: (orderDetails: FoodOrder) => void;
  setProgramBudget?: (budget: ProgramBudget[]) => void;
  setProgramMAXBudget?: (budget: number) => void;
  setProgramOrdersCost?: (cost: number) => void;
  /** A vendor code; keys the hard-coded `pickList` of bundle names. */
  vendor?: string;
  /** The vendor's display name, uppercased to key `PROGRAM_VENDOR_CONFIG`. */
  vendorName?: string;
};

function AddOrder(props: AddOrderProps) {
  const _authInfo = useAuthContext();
  const [mealEquivalentsOptions, _setMealEquivalentsOptions] = useState<NumericOption[]>([]);
  const [_referralId, setReferralId] = useState('');
  const [budgetOverrideChecked, setBudgetOverrideChecked] = useState(false);

  useEffect(() => {
    //let mealEquivalentsOptions = [];
    if (PROGRAMS_MEQ.includes(props.org!)) {
      mealEquivalentsOptions.push({ key: 0, text: 0, value: 0 });
    } else {
      mealEquivalentsOptions.push({ key: 'NA', text: 'NA', value: 'NA' });
    }
    for (let i = 1; i <= 30; i++) {
      mealEquivalentsOptions.push({ key: i, text: i, value: i });
    }
    //network call again on mount, in case estiamted delivery date didnt populate
    props.estimatedDeliveryDateMutate();
  }, []);

  /**
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. Four independent absences, all on the
   * path that resolves the member's food budget before the order form opens.
   *
   * `item.status.toUpperCase()` — `ReferralSummary.status` is optional
   * (`types/patient-data.ts`), so any referral row lacking a status throws a
   * TypeError inside the `filter`. The throw is inside a `useEffect` with no
   * boundary, so React unmounts the tree: the whole food tab goes blank rather
   * than the one referral being skipped.
   *
   * FOURTH OF FOUR COPIES of this predicate. The others are `food/orders/index.tsx`,
   * `patient/index.tsx`, and — extracted rather than inline — `isInProgress` in
   * `food/catalog/item/foodBundleItems.tsx`, whose parameter is deliberately left
   * unannotated so the same crash survives there. All four must change together or
   * not at all: guarding one site converts a loud crash into a silently empty
   * referral list at that site only, which bills the order to no referral.
   *
   * `setProgramMAXBudget` — NOT passed at all on the food-card path
   * (`components/common/foodCard/index.tsx` forwards `setProgramOrdersCost` but
   * omits its `MAXBudget` twin), so this is a call on `undefined`. Reaching this
   * effect from a food card therefore throws before the budget ceiling is ever
   * published to the parent, and the same unmount follows.
   *
   * `setProgramOrdersCost` is optional in the same chain but IS passed by every
   * caller today; it is bang-asserted here only because its declaration mirrors
   * the twin above.
   */
  useEffect(() => {
    const referralData = props.allReferralData?.filter(
      (item) => item.status!.toUpperCase() === 'IN-PROGRESS',
    );
    setReferralId(
      (referralData?.length as number) > 0 ? (referralData![0]?.referralId as string) : '',
    );

    const budget = getProgramBudget(
      (referralData?.length as number) > 0 ? referralData![0]?.referralId : '',
      props.programBudget!,
    );

    props.setProgramOrdersCost!(budget.programOrderCost!);
    props.setProgramMAXBudget!(budget.programFoodBudget!);
  }, [props.allReferralData, props.programBudget]);

  const pickList: Record<string, string[] | undefined> = {
    AB: [
      'Kickstarter Option A',
      'Pantry Option 1A',
      'Grocery 35A',
      'Grocery 35B',
      'Standard Grocery Bundle',
      'Pantry Only Bundle',
      'Groceries bundle & prepared meals',
    ],
    MF: [
      'Kickstarter Pantry Kit',
      'Pantry Kit',
      'Monthly Groceries A',
      'Monthly Groceries B',
      'Monthly#1 A',
      'Monthly#1 B',
    ],
    NM: [
      'W1B1X10',
      'W1B2X10',
      'W1B3X10',
      'W1B1X15',
      'W1B2X15',
      'W1B3X15',
      'W2B1X10',
      'W2B2X10',
      'W2B3X10',
      'W2B1X15',
      'W2B2X15',
      'W2B3X15',
      'W3B1X10',
      'W3B2X10',
      'W3B3X10',
      'W3B1X15',
      'W3B2X15',
      'W3B3X15',
      'W4B1X10',
      'W4B2X10',
      'W4B3X10',
      'W4B1X15',
      'W4B2X15',
      'W4B3X15',
    ],
    UM: [
      'Pantry Bundle A ($75)',
      'Produce Bundle ($60)',
      'Protein Bundle A ($70)',
      'Snack Bundle A ($45)',
      'Pantry Bundle B ($60)',
      'Snack Bundle B ($65)',
      'Protein Bundle B ($60)',
    ],
    MM: ['Grocery Bundle CT'],
    MH: ['SC 10 Meal Bundle', 'NC Bundle 1', 'NC Bundle 2'],
    FL: ['Fresh List Produce Box'],
    BM: ['Biggers Market Produce Box'],
    KB: ['Kebribayah Grocery Box'],
    RK: ['Bundle 1', 'Bundle 2', 'Bundle 3', 'Bundle 4'],
    MWUYF: [
      'Produce Only',
      'Produce A + Pantry A',
      'Produce A + Pantry B',
      'Produce B + Pantry A',
      'Produce B + Pantry B',
    ],
    SSHE: ['10-Meal Bundle', '7d Meal Plan-2k', '4d Meal Plan-2k'],
    HM: ['Shelf Stable Puree Mixed 10 Pack'],
    ML: [
      'VEGETARIAN BUNDLE (GF/DF)',
      'BUNDLE 1 (GF/DF)',
      'BUNDLE 2 (GF/DF)',
      'BUNDLE 3 (GF/DF)',
      '14 CT BUNDLE (GF/DF)',
    ],
    HH: ['HRx Standard Mini', 'HRx Easy to Eat Mini', 'HRx Standard Full', 'HRx Easy to Eat Full'],
    // FFSH setup as catalog, below options are not needed for now
    FFSH: [
      'Magnolia (Veg) - Med',
      'Soul Food - Med',
      'Sabor - Med',
      'Magnolia (Veg) - Lg',
      'Soul Food - Lg',
      'Sabor - Lg',
    ],
    Other: [
      'Prepared Meal',
      'Produce Box',
      'Pantry Kit',
      'Meal Kit',
      'KickStarter Kit',
      'Grocery Bundle',
      'Snack Pack',
      'Groceries bundle & prepared meals',
      'Produce + Pantry',
      'Supplement Pack',
      'Kitchen Supplies',
    ],
  };

  const [errorMsg, setErrorMsg] = useState('Please fill all the mandatory fields');
  const [state, setState] = useState<{ noteText?: string; allergens?: string }>({
    noteText: '',
    allergens: '',
  });
  const [loadFlag, setLoadFlag] = useState(false);
  const [ebtPending, setEbtPending] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const isBundle: boolean = [
    'AB',
    'NM',
    'UM',
    'MM',
    'MH',
    'FL',
    'BM',
    'KB',
    'RK',
    'MWUYF',
    'SSHE',
    'HM',
    'HH',
    'FFSH',
    'SSHE',
  ].includes(props.vendor!);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    { name, value }: InputOnChangeData,
  ) => {
    setState({ [name]: value });
    props.handleChange(e, name as string);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent 'e' and 'E' characters
    if (e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  const handleItemCheck = (_event: unknown, _data: unknown, _quantity: unknown) => {
    setBudgetOverrideChecked(!budgetOverrideChecked);
    //setOverrideFoodPrice(!overrideFoodPrice);
  };

  // Declared async so callers get a promise; removing async changes the return type from a promise
  // to a value.
  // eslint-disable-next-line @typescript-eslint/require-await
  const onModalLoad = async () => {
    setEbtPending(false);
    setValidationError(false);
    setLoadFlag(false);
    props.setOpen(true);

    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. `vendorName` is optional here and
     * reaches this component from the parent's own optional prop, so opening the
     * add-order modal for a vendor whose display name never resolved throws a
     * TypeError on `.toUpperCase()` — inside an async `onOpen`, so the rejection is
     * unhandled and the modal opens with no prices seeded rather than reporting a
     * failure.
     *
     * The two `price` reads below are separate: the surrounding `if` tests the very
     * same subscript chain, so the value is present by construction. That is a real
     * invariant the compiler cannot follow across two syntactically distinct but
     * identical optional chains, which is why these are asserted rather than noted
     * as absences.
     */
    if (
      PROGRAM_VENDOR_CONFIG?.[props.org as string]?.[props.vendorName!.toUpperCase()]?.['PRICE']
    ) {
      const price: VendorDefaultPrice | undefined =
        PROGRAM_VENDOR_CONFIG?.[props.org as string]?.[props.vendorName!.toUpperCase()]?.['PRICE'];
      props.setOrderDetails({
        ...props.orderDetails,
        foodPricing: price!.DEFAULT_FOOD_PRICE!,
        shippingFees: price!.DEFAULT_SHIPPING_PRICE,
        estimatedDeliveryDate: moment(props?.estimatedDeliveryDateRange).format('YYYY-MM-DD'),
      });
    } else if (
      PROGRAM_VENDOR_CONFIG?.['DEFAULT']?.[
        props.vendorName!.toUpperCase() as keyof typeof PROGRAM_VENDOR_CONFIG.DEFAULT
      ]?.['PRICE'] as VendorDefaultPrice | undefined
    ) {
      const price = PROGRAM_VENDOR_CONFIG?.['DEFAULT']?.[
        props.vendorName!.toUpperCase() as keyof typeof PROGRAM_VENDOR_CONFIG.DEFAULT
      ]?.['PRICE'] as VendorDefaultPrice | undefined;
      props.setOrderDetails({
        ...props.orderDetails,
        foodPricing: price!.DEFAULT_FOOD_PRICE!,
        shippingFees: price!.DEFAULT_SHIPPING_PRICE,
        estimatedDeliveryDate: moment(props?.estimatedDeliveryDateRange).format('YYYY-MM-DD'),
      });
    } else {
      props.setOrderDetails({
        ...props.orderDetails,
        foodPricing: 0,
        shippingFees: 0,
        estimatedDeliveryDate: moment(props?.estimatedDeliveryDateRange).format('YYYY-MM-DD'),
      });
    }

    if (props.estimatedDeliveryDateRange) {
      setLoadFlag(true);
    }
  };

  const { noteText, allergens } = state;

  return (
    <Modal
      as={Form}
      onOpen={() => onModalLoad()}
      onClose={() => props.setOpen(false)}
      open={props.open!}
      closeOnDimmerClick={false}
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
        setValidationError(false);
        props.orderDetails.status = 'new';
        const newOrderPrice = calculateMealPricing(props.orderDetails, false, props);
        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. Same absent-budget shape as the
         * update modal. `programOrdersCost` is forwarded from the parent's optional
         * prop, so on any path that did not resolve a program budget this is
         * `undefined + newOrderPrice` = `NaN`. `tempSetPrice` is then `NaN`, and the
         * ceiling check below (`NaN > props.programMAXBudget`) is false, so the order
         * is placed without the budget ever being enforced — permissive, and silent.
         */
        const tempSetPrice = (Math.round(props.programOrdersCost! + newOrderPrice) * 100) / 100; // Round to 2 decimal places
        let goodToPlaceOrder = true;
        if (
          (!ebtPending &&
            ((!isBundle && props.orderDetails.foodTypeName === undefined) ||
              (isBundle && props.orderDetails.bundleName === undefined) ||
              // (isBundle && props.orderDetails.foodType === undefined) ||
              props.orderDetails.orderType === undefined ||
              props.orderDetails.bundleQuantity === undefined ||
              props.orderDetails.mealEquivalents === undefined ||
              props.orderDetails.dtcOrderReason === undefined)) ||
          !props.orderDetails.estimatedDeliveryDate ||
          props.orderDetails.estimatedDeliveryDate === 'Invalid date'
        ) {
          setValidationError(true);
          setErrorMsg('Please fill all the mandatory fields');
          goodToPlaceOrder = false;
          props.setOpen(true);
        } else if (
          props.orderDetails.foodPricing &&
          parseFloat(String(props.orderDetails.foodPricing)) < 0
        ) {
          setValidationError(true);
          setErrorMsg('Food price cannot be negative');
          props.setOpen(true);
          goodToPlaceOrder = false;
        } else if (
          PROGRAMS_MEQ.includes(props.org!) &&
          !ebtPending &&
          // Cristina wants Gap orders to be considered for MEQs - 07152024
          // props.orderDetails.orderType.toUpperCase() !== 'GAP ORDER' &&
          parseInt(String(props.programMAXMealEquivalents)) <
            // Established, not asserted: this arm requires `!ebtPending`, and the
            // validation above returns for `!ebtPending && mealEquivalents ===
            // undefined`. The compiler cannot carry that through the negated
            // disjunction it is written as.
            (props.orderDetails.mealEquivalents as number) +
              parseInt(String(props.ordersMealEquivalents))
        ) {
          setValidationError(true);
          setErrorMsg(
            'The total Meal Equivalents will exceed the contractual agreement(Max Meal Equivalents)',
          );
          props.setOpen(true);
          goodToPlaceOrder = false;
        }
        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. `programMAXBudget` is optional and
         * absent wherever no program budget resolved, so `tempSetPrice > undefined`
         * is false and the order is placed with no ceiling applied. When it IS
         * absent the message below would also have interpolated `undefined` into the
         * dollar figure shown to the care manager — unreachable only because the
         * guard it sits behind can never be true in that state.
         */
        if (!budgetOverrideChecked && tempSetPrice > props.programMAXBudget!) {
          // else if (tempSetPrice > props.programMAXBudget) {
          setValidationError(true);
          setErrorMsg(
            `The total order cost will exceed the contractual agreement(Max Budget: $${props.programMAXBudget})`,
          );
          props.setOpen(true);
          goodToPlaceOrder = false;
        } else if (
          //to show error message if the users uses key, which will add all the options together
          (props.orderDetails?.bundleName?.length ?? 0) > 50 ||
          (props.orderDetails?.flavorName?.length ?? 0) > 30 ||
          (props.orderDetails?.foodTypeName?.length ?? 0) > 50 ||
          (props.orderDetails?.orderType?.length ?? 0) > 20 ||
          isNaN(props.orderDetails?.mealEquivalents as number)
        ) {
          setValidationError(true);
          const orderCategory: Record<string, number | undefined> = {
            bundleName: 25,
            flavorName: 30,
            foodTypeName: 35,
            orderType: 18,
          };
          const orderCategoryMatch: Record<string, string | undefined> = {
            bundleName: 'Order Name',
            flavorName: 'Flavor Name',
            foodTypeName: 'Food Type',
            orderType: 'Order Type',
          };
          const invalidCategory: string[] = [];
          // Iterating the key list itself rather than by index: the `[i]` read was
          // `string | undefined` under `noUncheckedIndexedAccess` even though the
          // loop bound is that array's own length. Same keys, same order.
          for (const category of Object.keys(orderCategory)) {
            if (
              ((props.orderDetails?.[category as keyof FoodOrder] as string | undefined)
                ?.length as number) > (orderCategory?.[category] as number)
            ) {
              invalidCategory.push(orderCategoryMatch[category]!);
            }
          }
          if (isNaN(props.orderDetails?.mealEquivalents as number)) {
            invalidCategory.push('Meal Equivalents');
          }
          const errorMessage = `Please reselect ${invalidCategory.join(', ')}.`;
          setErrorMsg(errorMessage);
          props.setOpen(true);
          goodToPlaceOrder = false;
        } else {
          if (goodToPlaceOrder) {
            props.addOrder(e);
          }
        }
      }}
      size="large"
      trigger={<Button content="New Order" color="linkedin" icon="add" />}
    >
      <Modal.Header>
        Create Order for {props.flatPatient && props.flatPatient.fullName}
      </Modal.Header>
      <Modal.Content scrolling>
        {validationError && (
          <Message header="Oops!. Something went wrong.." content={errorMsg} negative />
        )}
        <Form.Group widths="equal">
          <Form.Field>
            <Checkbox
              label="EBT Pending Order"
              checked={ebtPending}
              id="ebtPending"
              name="ebtPending"
              floated="left"
              onClick={() => setEbtPending(!ebtPending)}
            />
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Input label="Vendor ID" readOnly value={props.vendor} />
          <Form.Input label="Vendor Name" readOnly value={props.vendorName} />
        </Form.Group>
        <Form.Group widths="equal">
          {isBundle && (
            <Form.Field
              control={Select}
              label="Order Name"
              autoComplete="off"
              selectOnBlur={false}
              required
              /*
               * SUSPECTED DEFECT — REPORTED, NOT FIXED. `pickList` carries five
               * vendor codes and this read is unguarded, so opening the add-order
               * form on a bundle vendor outside that set throws. The enclosing
               * `isBundle` gate does not establish membership.
               *
               * The `!` keeps the throw rather than moving it: a `?.` would render
               * an Order Name dropdown with NO options and a `required` marker,
               * which a care manager cannot satisfy and cannot diagnose.
               */
              options={pickList[mapKey(props.vendor)]!.map((d: string) => {
                return { key: d, text: d.toUpperCase(), value: d };
              })}
              onChange={(e: React.SyntheticEvent<HTMLElement>, prop: DropdownProps) =>
                props.handleChange(e, 'bundleName', prop.value)
              }
              name="bundleName"
            />
          )}
          {props.vendor !== 'AB' &&
            props.vendor !== 'NM' &&
            props.vendor !== 'UM' &&
            props.vendor !== 'KB' && (
              <Form.Field
                control={Select}
                label="Food Type"
                autoComplete="off"
                selectOnBlur={false}
                required
                options={pickList[props.vendor === 'MF' ? 'MF' : 'Other']!.map((d: string) => {
                  return { key: d, text: d.toUpperCase(), value: d };
                })}
                name="foodTypeName"
                onChange={(e: React.SyntheticEvent<HTMLElement>, prop: DropdownProps) =>
                  props.handleChange(e, 'foodTypeName', prop.value)
                }
              />
            )}
          {/* <Form.Field
            control={Select}
            label="Flavor Name"
            autoComplete="off"
            selectOnBlur={false}
            options={[
              'All American',
              'All American - Vegetarian',
              'TexMex',
              'TexMex - Vegetarian',
              'Mediterranean',
              'Mediterranean - Vegetarian',
              'Renal',
              'N/A',
            ].map((d: string) => {
              return { key: d, text: d.toUpperCase(), value: d };
            })}
            onChange={(e: React.SyntheticEvent<HTMLElement>, prop: DropdownProps) => props.handleChange(e, 'flavorName', prop.value)}
            name="flavorName"
          /> */}
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Field
            label="Order Type"
            autoComplete="off"
            width={3}
            selectOnBlur={false}
            options={['Standard Order', 'Gap Order', 'Promo', 'Other', 'Other Goods'].map((x) => {
              return { key: x, text: x.toUpperCase(), value: x };
            })}
            control={Select}
            name="orderType"
            required
            onChange={(e: React.SyntheticEvent<HTMLElement>, prop: DropdownProps) =>
              props.handleChange(e, 'orderType', prop.value)
            }
          />
          <Form.Field
            label="DTC Order Reason"
            autoComplete="off"
            width={3}
            selectOnBlur={false}
            options={[
              'NOT DTC',
              'Allergies',
              'Address Issues',
              'Delivery Issues',
              'Food Insecure',
              'Member Satisfaction',
              'Cultural Food Preference',
              'Clinical Need',
            ].map((x) => {
              return { key: x, text: x.toUpperCase(), value: x };
            })}
            control={Select}
            name="dtcOrderReason"
            required
            onChange={(e: React.SyntheticEvent<HTMLElement>, prop: DropdownProps) =>
              props.handleChange(e, 'dtcOrderReason', prop.value)
            }
          />

          <Form.Input
            autoComplete="off"
            label="Quantity"
            width={3}
            type="number"
            min={0}
            max={15} // Limit to 15 for bundle orders
            required
            name="bundleQuantity"
            onChange={(e: React.ChangeEvent<HTMLInputElement>, prop: InputOnChangeData) =>
              props.handleChange(e, 'bundleQuantity', prop.value)
            }
          />
          <Form.Dropdown
            label="Meal Equivalents"
            placeholder="Select Meal Equivalent"
            required
            selection
            width={3}
            name="mealEquivalents"
            options={mealEquivalentsOptions}
            onChange={(e: React.SyntheticEvent<HTMLElement>, prop: DropdownProps) =>
              props.handleChange(e, 'mealEquivalents', prop.value)
            }
          />
        </Form.Group>
        <Form.Group>
          <Form.Input
            autoComplete="off"
            label="Food Price"
            type="number"
            onKeyDown={handleKeyDown}
            name="foodPricing"
            onChange={(e: React.ChangeEvent<HTMLInputElement>, prop: InputOnChangeData) =>
              props.handleChange(e, 'foodPricing', prop.value)
            }
            defaultValue={props.orderDetails.foodPricing}
            value={props.orderDetails.foodPricing}
          />
          <Form.Input
            autoComplete="off"
            label="Shipping Fee"
            type="text"
            name="shippingFees"
            onChange={(e: React.ChangeEvent<HTMLInputElement>, prop: InputOnChangeData) =>
              props.handleChange(e, 'shippingFees', prop.value)
            }
            defaultValue={props.orderDetails.shippingFees}
            value={props.orderDetails.shippingFees}
          />
          {
            <SemanticDatepicker
              disabled={!loadFlag}
              required
              autoComplete="false"
              onChange={(e, value) => props.handleEstimateDeliveryDateChange(e, value)}
              datePickerOnly
              name="estimatedDeliveryDate"
              value={props.estimatedDeliveryDateRange}
              clearable={false}
              format="MM/DD/YYYY"
              id="form-input-control-food"
              label="Food Expected By"
              className="dobCalendar"
            />
          }
          <Form.Checkbox
            autoComplete="off"
            label="Budget Override"
            type="checkbox"
            name="budgetOverrideChecked"
            // onChange={(e) => handleChange(e, 'budgetOverrideChecked')}
            defaultValue={false}
            style={{
              backgroundColor: budgetOverrideChecked ? '#e6f3ff' : 'transparent',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: budgetOverrideChecked ? '2px solid #2185d0' : '2px solid transparent',
              color: '#2185d0',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
            onClick={(event, data) => handleItemCheck(event, data, 'budgetOverrideChecked')}
          />
        </Form.Group>
        <Form.Group>
          <Form.Input
            label="Note:"
            width={8}
            rows={3}
            control={TextArea}
            name="noteText"
            value={noteText}
            cols={3}
            placeholder="Add Note"
            onChange={handleChange}
          />
          <Form.Input
            label="Allergies:"
            width={8}
            rows={3}
            control={TextArea}
            name="allergens"
            value={allergens}
            cols={3}
            placeholder="Add Allergies"
            onChange={handleChange}
          />
        </Form.Group>
        <Modal.Actions>
          <Button content="Create" type="submit" positive />
          <Button content="Close" onClick={() => props.setOpen(false)} negative />
        </Modal.Actions>
      </Modal.Content>
    </Modal>
  );
}

export default AddOrder;
